import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BettingEventDetailDto, BettingFinalizationRequest, BettingOption, BettingParticipationRequest } from '@playball/common/dto/Betting';
import { useAuth } from '../../context/AuthContext';
import { DataResponse } from '@playball/common/dto/Common';
import CommonUtil from '../../util/CommonUtil';

const BettingDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user, fetchApi } = useAuth();
    const [event, setEvent] = useState<BettingEventDetailDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedOption, setSelectedOption] = useState<BettingOption | null>(null);
    const [participationLoading, setParticipationLoading] = useState<boolean>(false);
    const hasFetched = useRef(false);

    const fetchBettingEvent = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data: DataResponse<BettingEventDetailDto> = await fetchApi(`/api/betting/${id}`);
            setEvent(data.data);
            if (data.data?.selected_option) {
                setSelectedOption(data.data?.selected_option);
            }
        } catch (e: any) {
            alert(e.message);
        } finally {
            setLoading(false);
        }
    }, [id, fetchApi]);

    useEffect(() => {
        if (hasFetched.current) {
            return;
        }
        hasFetched.current = true;
        fetchBettingEvent();
    }, [fetchBettingEvent]);

    const finalizeBettingEvent = useCallback(async (option: BettingOption) => {
        if (!event) return;

        if (!window.confirm('승리 처리를 하시겠습니까? 취소는 불가합니다')) return;

        setParticipationLoading(true);
        try {
            const requestBody: BettingFinalizationRequest = { winning_option: option };
            await fetchApi(`/api/betting/${id}/finalize`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });
            alert('승리 처리 완료!');
            await fetchBettingEvent(); // 페이지 새로고침 대신 데이터 다시 로드
        } catch (e: any) {
            alert(`승리 처리 실패: ${e.message}`);
        } finally {
            setParticipationLoading(false);
        }
    }, [event, fetchApi, id, fetchBettingEvent]);

    const handleParticipate = useCallback(async (option: BettingOption) => {
        if (!event) return;

        if (!window.confirm('참가비를 지불하고 베팅하시겠습니까? 취소는 불가합니다')) return;

        setParticipationLoading(true);
        try {
            const requestBody: BettingParticipationRequest = { selected_option: option };
            // 성공 시, 이벤트 정보를 다시 불러오거나 상태를 업데이트
            const updatedEvent: BettingEventDetailDto = await fetchApi(`/api/betting/${id}/participate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });
            setEvent(updatedEvent);
            setSelectedOption(option);
            alert('베팅에 참여했습니다!');
        } catch (e: any) {
            alert(`베팅 참여 실패: ${e.message}`);
        } finally {
            setParticipationLoading(false);
        }
    }, [event, fetchApi, id]);

    if (loading) {
        return <div className="text-center mt-5">로딩 중...</div>;
    }

    if (!event) {
        return <div className="alert alert-warning mt-5">이벤트를 찾을 수 없습니다.</div>;
    }

    const isScheduledOrInProgress = event.status === 'scheduled' || event.status === 'inprogress';
    const isClosedOrFinished = event.status === 'closed' || event.status === 'finished';

    return (
        <>
            <section className="guide-hero-section py-5">
                <div className="container text-center">
                    <h1 className="display-5 fw-bold">베팅 이벤트</h1>
                </div>
            </section>

            <div className="container my-5">
                <div className="card shadow-sm">
                    <div className="card-body p-4">
                        <h2 className="card-title text-center mb-3 fw-bold">{event.title}</h2>
                        <div className="text-center text-muted mb-3">
                            <p className="mb-1"><strong>기간:</strong> {CommonUtil.getLocalDateTimeString(event.start_at)} ~ {CommonUtil.getLocalDateTimeString(event.end_at)}</p>
                            <p className="mb-1"><strong>참가비:</strong> <span className="text-danger fw-bold">{event.price}</span> 포인트</p>
                        </div>
                        <div className="d-flex justify-content-center mb-4">
                            <span className={`badge fs-5 p-2 rounded-pill bg-${isScheduledOrInProgress ? 'warning' : 'success'}`}>
                                {event.status === 'scheduled' ? '예정' : event.status === 'inprogress' ? '진행 중' : event.status === 'closed' ? '마감' : '종료'}
                            </span>
                        </div>
                    </div>

                    <div className="card-body bg-light p-4">
                        <div className="row align-items-stretch">
                            {/* Option A */}
                            <div className="col-md-5 mb-3 mb-md-0">
                                <div className={`card text-center h-100 ${selectedOption === 'A' ? 'border-primary border-3' : ''} ${selectedOption && selectedOption !== 'A' ? 'opacity-50' : 'shadow-sm'}`}>
                                    <div className="card-body d-flex flex-column justify-content-between p-4">
                                        <div>
                                            <h4 className="card-title fw-bold">{event.option_a}</h4>
                                            {isScheduledOrInProgress && <p className="card-text text-muted mt-3">승리 시 예상 수익: <span className="fw-bold text-success">{event.option_a_expected_reward}</span> 포인트</p>}
                                        </div>
                                        {selectedOption === 'A' && <p className="text-primary fw-bold mt-3">✔️ 당신의 선택</p>}
                                    </div>
                                    {isScheduledOrInProgress && !selectedOption && (
                                        <div className="card-footer">
                                            <button className="btn btn-primary btn-lg w-100" onClick={() => handleParticipate('A')} disabled={participationLoading || event.status !== 'inprogress'}>
                                                {event.status === 'scheduled' && '베팅 시작 전'}
                                                {event.status === 'inprogress' && (participationLoading ? '처리 중...' : '베팅하기')}
                                            </button>
                                            {event.status === 'scheduled' && <div className="form-text small mt-1">베팅은 시작 시간 이후에 가능합니다.</div>}
                                        </div>
                                    )}
                                    {!isScheduledOrInProgress && user?.is_admin && !event.winning_option && (
                                        <div className="card-footer">
                                            <button className="btn btn-primary btn-lg w-100" onClick={() => finalizeBettingEvent('A')} disabled={participationLoading || event.status !== 'closed'}>
                                                승리 처리
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-2 d-none d-md-flex justify-content-center align-items-center">
                                <span className="display-4 fw-bold text-muted">VS</span>
                            </div>

                            {/* Option B */}
                            <div className="col-md-5">
                                <div className={`card text-center h-100 ${selectedOption === 'B' ? 'border-primary border-3' : ''} ${selectedOption && selectedOption !== 'B' ? 'opacity-50' : 'shadow-sm'}`}>
                                    <div className="card-body d-flex flex-column justify-content-between p-4">
                                        <div>
                                            <h4 className="card-title fw-bold">{event.option_b}</h4>
                                            {isScheduledOrInProgress && <p className="card-text text-muted mt-3">승리 시 예상 수익: <span className="fw-bold text-success">{event.option_b_expected_reward}</span> 포인트</p>}
                                        </div>
                                        {selectedOption === 'B' && <p className="text-primary fw-bold mt-3">✔️ 당신의 선택</p>}
                                    </div>
                                    {isScheduledOrInProgress && !selectedOption && (
                                        <div className="card-footer">
                                            <button className="btn btn-primary btn-lg w-100" onClick={() => handleParticipate('B')} disabled={participationLoading || event.status !== 'inprogress'}>
                                                {event.status === 'scheduled' && '베팅 시작 전'}
                                                {event.status === 'inprogress' && (participationLoading ? '처리 중...' : '베팅하기')}
                                            </button>
                                            {event.status === 'scheduled' && <div className="form-text small mt-1">베팅은 시작 시간 이후에 가능합니다.</div>}
                                        </div>
                                    )}
                                    {!isScheduledOrInProgress && user?.is_admin && !event.winning_option && (
                                        <div className="card-footer">
                                            <button className="btn btn-primary btn-lg w-100" onClick={() => finalizeBettingEvent('B')} disabled={participationLoading || event.status !== 'closed'}>
                                                승리 처리
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Result Section */}
                    {isClosedOrFinished && (
                        <div className="card-body p-4">
                            <h3 className="card-title text-center mb-4 fw-bold">최종 결과</h3>
                            {event.winning_option ? (
                                <div className={`alert ${event.selected_option && event.selected_option === event.winning_option ? 'alert-success' : 'alert-info'}`}>
                                    <h5 className="alert-heading fw-bold">
                                        {event.selected_option && event.selected_option === event.winning_option ? '🎉 축하합니다! 적중했습니다! 🎉' : '베팅 결과'}
                                    </h5>
                                    <p><strong>승리 옵션: {event.winning_option === 'A' ? event.option_a : event.option_b}</strong></p>
                                    <hr/>
                                    {event.selected_option ? (
                                        <p className="mb-0">
                                            <strong>나의 선택: {event.selected_option === 'A' ? event.option_a : event.option_b}</strong>
                                            {event.selected_option === event.winning_option 
                                                ? <span className="fw-bold text-success"> 👉 {event.winning_reward} 포인트를 획득했습니다!</span> 
                                                : <span className="text-danger fw-bold"> 👉 아쉽지만 다음 기회에.</span>
                                            }
                                        </p>
                                    ) : (
                                        <p className="mb-0">이번 베팅에 참여하지 않았습니다.</p>
                                    )}
                                </div>
                            ) : (
                                <div className="alert alert-info">
                                    아직 결과가 집계되지 않았습니다.
                                </div>
                            )}
                        </div>
                    )}

                    <div className="card-footer text-center bg-light">
                        <button className="btn btn-secondary" onClick={() => navigate('/betting')}>목록으로 돌아가기</button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BettingDetailPage;
